/**
 * Virtual Try-On API Client
 * 
 * This module provides client functions for the new virtual try-on endpoints:
 * - Single virtual try-on
 * - Batch virtual try-on
 * - Multi-pose virtual try-on
 * - Fashion model generation
 * 
 * Based on OpenAPI specification at https://api.gomens.ai/docs
 */

import React from 'react';
import { api } from './apiClient';

/**
 * Pose presets available for model generation and virtual try-on
 */
export const PosePresets = {
  FRONT_FULL_BODY_RELAXED: 'front_full_body_relaxed',
  FRONT_THREE_QUARTER_HAND_ON_HIP: 'front_three_quarter_hand_on_hip',
  WALKING_SIDE_PROFILE: 'walking_side_profile',
  LEANING_WALL_ARMS_CROSSED: 'leaning_wall_arms_crossed',
  TURNING_BACK_OVER_SHOULDER: 'turning_back_over_shoulder',
  HANDS_IN_POCKETS_CASUAL: 'hands_in_pockets_casual',
  SEATED_ON_STOOL: 'seated_on_stool',
  STREET_STYLE_MID_STEP: 'street_style_mid_step',
  CROSS_LEG_SITTING_FLOOR: 'cross_leg_sitting_floor',
  HALF_BODY_CLOSE_UP: 'half_body_close_up',
};

/**
 * Garment categories for virtual try-on
 */
export const GarmentCategory = {
  TOP: 'top',
  BOTTOM: 'bottom',
  DRESS: 'dress',
  TOP_AND_BOTTOM: 'top_and_bottom',
};

/**
 * Garment presentation types
 */
export const GarmentPresentation = {
  FLAT_LAY: 'flat_lay',
  MANNEQUIN: 'mannequin',
  GHOST_MANNEQUIN: 'ghost_mannequin',
};

/**
 * Output quality options
 */
export const OutputQuality = {
  ONE_K: '1K',    // 1024x1024 - fast, works with all models
  TWO_K: '2K',    // 2048x2048 - requires Gemini 3 Pro
  FOUR_K: '4K',   // 4096x4096 - requires Gemini 3 Pro, highest quality
};

/**
 * Aspect ratio options
 */
export const AspectRatio = {
  SQUARE: '1:1',           // 1:1 - Square format
  PORTRAIT: '3:4',         // 3:4 - Portrait format
  LANDSCAPE_43: '4:3',     // 4:3 - Landscape format
  WIDESCREEN: '16:9',      // 16:9 - Widescreen format
};

/**
 * Single Virtual Try-On
 * 
 * Perform virtual try-on for a single combination of garment + model(s) + background.
 * 
 * @param {Object} params
 * @param {File|Blob} params.garmentImage - Garment image file (JPEG/PNG/WebP)
 * @param {string} params.garmentCategory - Category: 'top', 'bottom', 'dress', or 'top_and_bottom'
 * @param {string} [params.garmentPresentation='flat_lay'] - Presentation type: 'flat_lay', 'mannequin', or 'ghost_mannequin'
 * @param {Array<File|Blob>} params.modelImages - Array of model/person image files (required, at least one)
 * @param {string} [params.modelPrompt] - Optional prompt to generate model
 * @param {File|Blob} [params.backgroundImage] - Optional background image file
 * @param {string} [params.outputQuality='1K'] - Output quality: '1K', '2K', or '4K'
 * @param {string} [params.aspectRatio='1:1'] - Aspect ratio: '1:1', '3:4', '4:3', or '16:9'
 * @param {string} [params.posePreset] - Optional pose preset (use PosePresets constants)
 * @param {Object} [params.headers={}] - Optional request headers (e.g., Authorization)
 * @returns {Promise<Object>} Response with { request_id, output_quality, pose_preset?, results: [{ model_index, success, image?: {data_base64}, image_url?: string, error? }], metadata }
 * 
 * Response Structure:
 * - results[].success: boolean - Whether the try-on was successful
 * - results[].image_url: string | null - URL to the result image (preferred)
 * - results[].image: {data_base64: string} | null - Base64 encoded image data (fallback)
 * - results[].error: string | null - Error message if success=false
 * 
 * @example
 * const result = await singleVirtualTryon({
 *   garmentImage: garmentFile,
 *   garmentCategory: GarmentCategory.TOP,
 *   garmentPresentation: GarmentPresentation.FLAT_LAY,
 *   modelImages: [modelFile1, modelFile2],
 *   outputQuality: OutputQuality.TWO_K,
 *   posePreset: PosePresets.FRONT_FULL_BODY_RELAXED
 * });
 * 
 * // Access results
 * result.results.forEach(r => {
 *   if (r.success) {
 *     const imageSource = r.image_url || r.image?.data_base64;
 *   }
 * });
 */
export async function singleVirtualTryon({
  garmentImage,
  garmentCategory,
  garmentPresentation = GarmentPresentation.FLAT_LAY,
  modelImages,
  modelPrompt,
  backgroundImage,
  outputQuality = OutputQuality.ONE_K,
  aspectRatio = AspectRatio.SQUARE,
  posePreset,
  headers = {},
}) {
  try {
    // Validate required parameters
    if (!garmentImage) {
      throw new Error('garmentImage is required');
    }
    if (!garmentCategory) {
      throw new Error('garmentCategory is required');
    }
    // API requires model_images array (at least one image is required)
    if (!modelImages || !Array.isArray(modelImages) || modelImages.length === 0) {
      throw new Error('modelImages array with at least one image is required');
    }

    console.log('[singleVirtualTryon] Request params:', {
      garmentCategory,
      garmentPresentation,
      outputQuality,
      posePreset,
      hasGarmentImage: !!garmentImage,
      modelImagesCount: modelImages?.length || 0,
      hasModelPrompt: !!modelPrompt,
      hasBackgroundImage: !!backgroundImage,
      headers: Object.keys(headers),
    });

    const formData = new FormData();
    
    formData.append('garment_image', garmentImage);
    formData.append('garment_category', garmentCategory);
    formData.append('garment_presentation', garmentPresentation);
    
    // Append model images (required field)
    modelImages.forEach((image, index) => {
      formData.append('model_images', image);
      console.log(`[singleVirtualTryon] Added model_image ${index + 1}:`, { 
        size: image.size, 
        type: image.type,
        name: image.name 
      });
    });
    
    if (modelPrompt) {
      formData.append('model_prompt', modelPrompt);
    }
    
    if (backgroundImage) {
      formData.append('background_image', backgroundImage);
      console.log('[singleVirtualTryon] Added background_image:', { 
        size: backgroundImage.size, 
        type: backgroundImage.type 
      });
    }
    
    formData.append('output_quality', outputQuality);
    formData.append('aspect_ratio', aspectRatio);
    
    if (posePreset) {
      formData.append('pose_preset', posePreset);
    }
    
    const response = await api.post('/tryon/single', formData, {
      headers: {
        // Don't set Content-Type for FormData - let browser set it with boundary
        ...headers,
      },
    });
    
    console.log('[singleVirtualTryon] Raw API response:', response);
    console.log('[singleVirtualTryon] Response data structure:', {
      status: response.status,
      statusText: response.statusText,
      hasData: !!response.data,
      dataType: typeof response.data,
      dataKeys: response.data ? Object.keys(response.data) : null,
      hasResults: !!(response.data && response.data.results),
      resultsCount: response.data?.results?.length || 0,
      firstResult: response.data?.results?.[0],
      requestId: response.data?.request_id,
    });
    
    return response.data;
  } catch (error) {
    console.error('[singleVirtualTryon] Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      requestHeaders: error.config?.headers,
      url: error.config?.url,
    });
    throw error;
  }
}

/**
 * Batch Virtual Try-On
 * 
 * Perform virtual try-on for multiple jobs concurrently (max 20 jobs per batch).
 * Images are downloaded from provided URLs.
 * 
 * @param {Object} params
 * @param {Array<Object>} params.jobs - Array of job objects (max 20)
 * @param {string} params.jobs[].job_id - Unique identifier for the job
 * @param {Object} params.jobs[].garment_image - Garment image reference
 * @param {string} params.jobs[].garment_image.url - URL to garment image
 * @param {string} params.jobs[].garment_image.category - Garment category
 * @param {string} [params.jobs[].garment_image.presentation='flat_lay'] - Garment presentation type
 * @param {Object} [params.jobs[].model_image] - Optional model image reference
 * @param {string} params.jobs[].model_image.url - URL to model image
 * @param {string} [params.jobs[].model_prompt] - Optional model generation prompt
 * @param {Object} [params.jobs[].background_image] - Optional background image reference
 * @param {string} params.jobs[].background_image.url - URL to background image
 * @param {string} [params.jobs[].output_quality='1K'] - Output quality
 * @param {string} [params.jobs[].pose_preset] - Optional pose preset
 * @returns {Promise<Object>} Response with { results: [{ job_id, success, output_quality?, pose_preset?, image?, error? }] }
 * 
 * @example
 * const result = await batchVirtualTryon({
 *   jobs: [
 *     {
 *       job_id: "job-001",
 *       garment_image: {
 *         url: "https://example.com/garment1.jpg",
 *         category: GarmentCategory.TOP,
 *         presentation: GarmentPresentation.FLAT_LAY
 *       },
 *       model_prompt: "Professional female model, tall and slim",
 *       output_quality: OutputQuality.TWO_K,
 *       pose_preset: PosePresets.FRONT_FULL_BODY_RELAXED
 *     },
 *     {
 *       job_id: "job-002",
 *       garment_image: {
 *         url: "https://example.com/garment2.jpg",
 *         category: GarmentCategory.DRESS
 *       },
 *       model_image: {
 *         url: "https://example.com/model.jpg"
 *       },
 *       output_quality: OutputQuality.ONE_K
 *     }
 *   ]
 * });
 */
export async function batchVirtualTryon({ jobs, headers = {} }) {
  if (!Array.isArray(jobs) || jobs.length === 0) {
    throw new Error('Jobs array is required and must not be empty');
  }
  
  if (jobs.length > 20) {
    throw new Error('Maximum 20 jobs per batch');
  }
  
  const response = await api.post('/tryon/batch', { jobs }, { headers });
  return response.data;
}

/**
 * Multi-Pose Virtual Try-On
 * 
 * Generate virtual try-on images across multiple poses for a single garment and model.
 * 
 * @param {Object} params
 * @param {File|Blob} params.garmentImage - Garment image file
 * @param {string} params.garmentCategory - Garment category
 * @param {string} [params.garmentPresentation='flat_lay'] - Garment presentation type
 * @param {File|Blob} [params.modelImage] - Optional model image file
 * @param {string} [params.modelPrompt] - Optional model generation prompt
 * @param {File|Blob} [params.backgroundImage] - Optional background image file
 * @param {string} [params.outputQuality='1K'] - Output quality
 * @param {Array<string>} [params.posePresets] - Array of specific pose presets (ignored if allStandardPoses is true)
 * @param {boolean} [params.allStandardPoses=false] - Generate all 10 standard poses
 * @param {Object} [params.headers={}] - Optional request headers (e.g., Authorization)
 * @returns {Promise<Object>} Response with { request_id, output_quality, poses: [{ pose_preset, image?, image_url? }] }
 * 
 * @example
 * // Generate specific poses
 * const result = await multiPoseVirtualTryon({
 *   garmentImage: garmentFile,
 *   garmentCategory: GarmentCategory.TOP,
 *   modelPrompt: "Professional female model",
 *   posePresets: [
 *     PosePresets.FRONT_FULL_BODY_RELAXED,
 *     PosePresets.WALKING_SIDE_PROFILE
 *   ],
 *   outputQuality: OutputQuality.TWO_K
 * });
 * 
 * // Generate all standard poses
 * const allPoses = await multiPoseVirtualTryon({
 *   garmentImage: garmentFile,
 *   garmentCategory: GarmentCategory.DRESS,
 *   modelImage: modelFile,
 *   allStandardPoses: true
 * });
 */
export async function multiPoseVirtualTryon({
  garmentImage,
  garmentCategory,
  garmentPresentation = GarmentPresentation.FLAT_LAY,
  modelImage,
  modelPrompt,
  backgroundImage,
  outputQuality = OutputQuality.ONE_K,
  posePresets,
  allStandardPoses = false,
  headers = {},
}) {
  const formData = new FormData();
  
  formData.append('garment_image', garmentImage);
  formData.append('garment_category', garmentCategory);
  formData.append('garment_presentation', garmentPresentation);
  
  if (modelImage) {
    formData.append('model_image', modelImage);
  }
  
  if (modelPrompt) {
    formData.append('model_prompt', modelPrompt);
  }
  
  if (backgroundImage) {
    formData.append('background_image', backgroundImage);
  }
  
  formData.append('output_quality', outputQuality);
  formData.append('all_standard_poses', allStandardPoses);
  
  if (posePresets && Array.isArray(posePresets)) {
    posePresets.forEach(preset => {
      formData.append('pose_presets', preset);
    });
  }
  
  const response = await api.post('/tryon/multi-pose', formData, {
    headers: {
      // Don't set Content-Type for FormData - let browser set it with boundary
      ...headers,
    },
  });
  
  return response.data;
}

/**
 * Generate Fashion Model
 * 
 * Generate fashion model images in various poses without garment try-on.
 * Useful for creating model references or pre-generating models for later try-on operations.
 * 
 * @param {Object} params
 * @param {string} params.modelPrompt - Prompt to generate the fashion model (e.g., "Professional female fashion model, tall and slim, 25 years old")
 * @param {Array<string>} [params.posePresets] - Array of specific pose presets (max 10, ignored if allStandardPoses is true)
 * @param {boolean} [params.allStandardPoses=false] - Generate all 10 standard poses
 * @param {string} [params.outputQuality='1K'] - Output quality: '1K', '2K', or '4K'
 * @param {Object} [params.headers={}] - Optional request headers (e.g., Authorization)
 * @returns {Promise<Object>} Response with { request_id, output_quality, poses: [{ pose_preset, image?, image_url? }] }
 * 
 * @example
 * // Generate specific poses
 * const result = await generateFashionModel({
 *   modelPrompt: "Professional male fashion model, athletic build, 28 years old, confident expression",
 *   posePresets: [
 *     PosePresets.FRONT_FULL_BODY_RELAXED,
 *     PosePresets.HANDS_IN_POCKETS_CASUAL,
 *     PosePresets.WALKING_SIDE_PROFILE
 *   ],
 *   outputQuality: OutputQuality.TWO_K
 * });
 * 
 * // Generate all standard poses
 * const allPoses = await generateFashionModel({
 *   modelPrompt: "Professional female model, elegant style, 25 years old",
 *   allStandardPoses: true,
 *   outputQuality: OutputQuality.FOUR_K
 * });
 */
export async function generateFashionModel({
  modelPrompt,
  posePresets = [],
  allStandardPoses = false,
  outputQuality = OutputQuality.ONE_K,
  headers = {},
}) {
  if (!modelPrompt) {
    throw new Error('modelPrompt is required');
  }
  
  if (!allStandardPoses && (!posePresets || posePresets.length === 0)) {
    throw new Error('Either provide posePresets array or set allStandardPoses to true');
  }
  
  if (posePresets.length > 10) {
    throw new Error('Maximum 10 poses per request');
  }
  
  const requestBody = {
    model_prompt: modelPrompt,
    all_standard_poses: allStandardPoses,
    output_quality: outputQuality,
  };
  
  if (!allStandardPoses && posePresets.length > 0) {
    requestBody.pose_presets = posePresets;
  }
  
  const response = await api.post('/fshmodel', requestBody, { headers });
  return response.data;
}

/**
 * Helper function to convert a File/Blob to base64 string
 * (Useful if you need to display images from the API responses)
 * 
 * @param {string} base64Data - Base64 encoded image data from API response
 * @param {string} mimeType - MIME type of the image (e.g., 'image/png')
 * @returns {string} Data URL that can be used in img src
 */
export function base64ToDataUrl(base64Data, mimeType = 'image/png') {
  return `data:${mimeType};base64,${base64Data}`;
}

/**
 * Helper function to create download link for base64 image
 * 
 * @param {string} base64Data - Base64 encoded image data
 * @param {string} filename - Desired filename for download
 * @param {string} mimeType - MIME type of the image
 */
export function downloadBase64Image(base64Data, filename, mimeType = 'image/png') {
  const dataUrl = base64ToDataUrl(base64Data, mimeType);
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Upload image to Google Cloud Storage for batch processing
 * 
 * @param {Object} params
 * @param {File|Blob} params.image - Image file to upload
 * @param {string} params.purpose - Purpose of the upload (e.g., 'garment', 'background')
 * @param {Object} params.headers - Authentication headers
 * @returns {Promise<{url: string}>} Uploaded image URL
 */
export async function uploadImage({ image, purpose = 'garment', headers }) {
  if (!image) {
    throw new Error('Image file is required');
  }

  const formData = new FormData();
  formData.append('image', image);
  formData.append('purpose', purpose);

  try {
    const response = await api.post('/tryon/upload-image', formData, {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Image upload error:', error);
    throw error;
  }
}

/**
 * Create a batch virtual try-on job using Gemini Batch API
 * 
 * @param {Object} params
 * @param {Array<Object>} params.jobs - Array of job configurations
 * Each job should have:
 *   - garment_url: GCS signed URL for garment image
 *   - garment_category: 'top', 'bottom', 'dress', or 'top_and_bottom'
 *   - garment_presentation: 'flat_lay', 'mannequin', or 'ghost_mannequin' (default: 'flat_lay')
 *   - model_image_url: GCS signed URL for model/pose image
 *   - background_url: Optional GCS signed URL for background image
 *   - output_quality: '1K', '2K', or '4K' (default: '1K')
 * @param {Object} params.headers - Authentication headers
 * @returns {Promise<{batch_id: string, state: string, total_requests: number, message: string}>} Batch job information
 */
export async function createBatchVirtualTryon({ jobs, headers }) {
  if (!jobs || !Array.isArray(jobs) || jobs.length === 0) {
    throw new Error('Jobs array is required and must not be empty');
  }

  if (jobs.length > 10000) {
    throw new Error('Maximum 10,000 jobs per batch');
  }

  try {
    const response = await api.post('/tryon/batch-process', { jobs }, { headers });
    return response.data;
  } catch (error) {
    console.error('Batch virtual try-on creation error:', error);
    throw error;
  }
}

/**
 * Get user's batch jobs
 * 
 * @param {Object} params
 * @param {Object} params.headers - Authentication headers
 * @returns {Promise<{batches: Array<Object>, count: number}>} List of user's batch jobs
 * 
 * Response includes:
 * - batch_id: string
 * - state: string (JOB_STATE_*)
 * - processing_status: string | null
 * - results_ready: boolean | null
 * - total_requests: number
 * - succeeded_requests: number
 * - failed_requests: number
 * - create_time: string | null
 * - start_time: string | null
 * - end_time: string | null
 * - created_at: string
 * - user_id: string
 * - input_image_ids: string[] | null
 * - cleaned_up_at: string | null
 * - images_cleaned_up: boolean | null
 * - images_deleted_count: number | null
 * - last_error: string | null
 * - last_error_at: string | null
 * - last_polled_at: string | null
 * - results_processed_at: string | null
 * - results_stored_count: number | null
 * - results_failed_count: number | null
 * - retry_count: number | null
 */
export async function getUserBatches({ headers }) {
  try {
    const response = await api.get('/tryon/batches', { headers });
    
    // Map backend response to frontend-expected field names
    const mappedBatches = (response.data.batches || []).map(batch => ({
      ...batch,
      // Map backend fields to UI-friendly names
      status: mapJobStateToStatus(batch.state),
      total_jobs: batch.total_requests || 0,
      completed_jobs: batch.succeeded_requests || 0,
      failed_jobs: batch.failed_requests || 0,
    }));
    
    return {
      ...response.data,
      batches: mappedBatches
    };
  } catch (error) {
    console.error('Get user batches error:', error);
    throw error;
  }
}

/**
 * Get batch status (metadata only, no results)
 * 
 * @param {Object} params
 * @param {string} params.batchId - Batch job ID (e.g., 'batches/123456789')
 * @param {Object} params.headers - Authentication headers
 * @returns {Promise<BatchStatusResponse>} Batch status metadata
 * 
 * **NOTE:** This endpoint returns status/metadata only. The `results` array is always empty.
 * Use `getBatchResults()` to fetch actual job results with images.
 * 
 * Response Schema (BatchStatusResponse):
 * - batch_id: string
 * - state: string (JOB_STATE_PENDING | JOB_STATE_RUNNING | JOB_STATE_SUCCEEDED | JOB_STATE_FAILED | JOB_STATE_CANCELLED | JOB_STATE_EXPIRED)
 * - total_requests: number
 * - succeeded_requests: number
 * - failed_requests: number
 * - create_time: string | null
 * - start_time: string | null
 * - end_time: string | null
 * - results: [] (always empty - use getBatchResults for actual results)
 * - processing_status: string | null
 * - results_ready: boolean | null
 * - last_polled_at: string | null
 * - results_processed_at: string | null
 * - results_stored_count: number | null
 * - results_failed_count: number | null
 * - retry_count: number | null
 * - last_error: string | null
 * - images_cleaned_up: boolean | null
 * 
 * Job States:
 * - JOB_STATE_PENDING: Waiting to be processed
 * - JOB_STATE_RUNNING: Currently processing
 * - JOB_STATE_SUCCEEDED: Completed successfully
 * - JOB_STATE_FAILED: Failed
 * - JOB_STATE_CANCELLED: Cancelled by user
 * - JOB_STATE_EXPIRED: Ran/pending for >48 hours
 */
export async function getBatchStatus({ batchId, headers }) {
  if (!batchId) {
    throw new Error('Batch ID is required');
  }

  try {
    // URL-encode the batch ID for HTTP path parameter
    // Backend FastAPI will automatically decode it: batches%2Fxxx -> batches/xxx
    const encodedBatchId = encodeURIComponent(batchId);
    const response = await api.get(`/tryon/batch-process/${encodedBatchId}`, { headers });
    const data = response.data;
    
    // Return BatchStatusResponse with all fields from the API
    // Note: results array is always empty from this endpoint
    return {
      // Core batch information
      batch_id: data.batch_id,
      state: data.state,
      total_requests: data.total_requests,
      succeeded_requests: data.succeeded_requests,
      failed_requests: data.failed_requests,
      
      // Timestamps
      create_time: data.create_time,
      start_time: data.start_time,
      end_time: data.end_time,
      
      // Results array (always empty - use getBatchResults for actual data)
      results: [],
      
      // Processing metadata
      processing_status: data.processing_status,
      results_ready: data.results_ready,
      last_polled_at: data.last_polled_at,
      results_processed_at: data.results_processed_at,
      results_stored_count: data.results_stored_count,
      results_failed_count: data.results_failed_count,
      retry_count: data.retry_count,
      last_error: data.last_error,
      images_cleaned_up: data.images_cleaned_up,
      
      // Computed fields for UI compatibility
      status: mapJobStateToStatus(data.state),
      total_jobs: data.total_requests,
      completed_jobs: data.succeeded_requests,
      failed_jobs: data.failed_requests,
      jobs: [],  // Empty - use getBatchResults() to fetch actual results
    };
  } catch (error) {
    console.error('Get batch status error:', error);
    throw error;
  }
}

/**
 * Get paginated batch results with actual job data
 * 
 * @param {Object} params
 * @param {string} params.batchId - Batch job ID
 * @param {Object} params.headers - Authentication headers
 * @param {number} [params.page=1] - Page number (1-indexed)
 * @param {number} [params.limit=50] - Results per page (max 100)
 * @param {boolean} [params.successOnly=false] - Filter to successful results only
 * @returns {Promise<{batch_id: string, page: number, limit: number, total_results: number, total_pages: number, results: Array<Object>}>} Paginated batch results
 * 
 * Each result contains:
 * - request_key: string
 * - success: boolean
 * - image_url: string | null
 * - image_file_id: string | null
 * - image_width: number | null
 * - image_height: number | null
 * - mime_type: string | null
 * - created_at: string | null
 * - error_code: string | null
 * - error_message: string | null
 */
export async function getBatchResults({ batchId, headers, page = 1, limit = 50, successOnly = false }) {
  if (!batchId) {
    throw new Error('Batch ID is required');
  }

  try {
    // URL-encode the batch ID for HTTP path parameter
    // Backend FastAPI will automatically decode it: batches%2Fxxx -> batches/xxx
    const encodedBatchId = encodeURIComponent(batchId);
    
    // Build query parameters
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (successOnly) {
      params.append('success_only', 'true');
    }
    
    const response = await api.get(`/tryon/batch-process/${encodedBatchId}/results?${params.toString()}`, { headers });
    const data = response.data;
    
    // Map results to include computed status field for UI
    return {
      batch_id: data.batch_id,
      page: data.page,
      limit: data.limit,
      total_results: data.total_results,
      total_pages: data.total_pages,
      results: (data.results || []).map(result => ({
        request_key: result.request_key,
        success: result.success,
        image_url: result.image_url,
        image_file_id: result.image_file_id,
        image_width: result.image_width,
        image_height: result.image_height,
        mime_type: result.mime_type,
        created_at: result.created_at,
        error_code: result.error_code,
        error_message: result.error_message,
        // Computed fields for UI compatibility
        status: result.success ? 'completed' : 'failed',
        result_url: result.image_url,
        file_id: result.image_file_id,
        width: result.image_width,
        height: result.image_height,
      })),
    };
  } catch (error) {
    console.error('Get batch results error:', error);
    throw error;
  }
}

/**
 * Download all batch results as a ZIP file (streaming from backend)
 * 
 * This is a more efficient implementation that streams a pre-built ZIP file
 * from the backend instead of downloading individual images and creating
 * the ZIP on the client side.
 * 
 * @param {Object} params
 * @param {string} params.batchId - Batch job ID (e.g., 'batches/123456789')
 * @param {Object} params.headers - Authentication headers (must include Authorization and x-user-id)
 * @returns {Promise<Blob>} ZIP file blob ready for download
 * 
 * @example
 * const zipBlob = await downloadBatchZipStream({
 *   batchId: 'batches/123456789',
 *   headers: {
 *     'Authorization': `Bearer ${token}`,
 *     'x-user-id': userId
 *   }
 * });
 * saveAs(zipBlob, `batch_${batchId}.zip`);
 */
export async function downloadBatchZipStream({ batchId, headers }) {
  if (!batchId) {
    throw new Error('Batch ID is required');
  }

  try {
    // URL-encode the batch ID for HTTP path parameter
    const encodedBatchId = encodeURIComponent(batchId);
    
    const response = await api.get(`/tryon/batch-download-stream/${encodedBatchId}`, {
      headers,
      responseType: 'blob', // Important: tell axios to expect binary data
    });
    
    return response.data; // Returns the blob directly
  } catch (error) {
    console.error('Download batch ZIP stream error:', error);
    throw error;
  }
}

/**
 * Map Gemini Batch API job state to simplified status
 * @param {string} state - Gemini Batch API state (e.g., 'JOB_STATE_PENDING')
 * @returns {string} Simplified status ('pending', 'processing', 'completed', 'failed')
 */
function mapJobStateToStatus(state) {
  const stateMap = {
    'JOB_STATE_PENDING': 'pending',
    'JOB_STATE_RUNNING': 'processing',
    'JOB_STATE_SUCCEEDED': 'completed',
    'JOB_STATE_FAILED': 'failed',
    'JOB_STATE_CANCELLED': 'failed',
    'JOB_STATE_EXPIRED': 'failed',
  };
  return stateMap[state] || 'pending';
}

/**
 * VirtualTryonClientProvider is a lightweight React wrapper to keep this file consistent
 * with other React modules and allow future context or configuration.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.children]
 */
export default function VirtualTryonClientProvider({ children = null }) {
  return <>{children}</>;
}
